import React, { useState } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import { risk_portal_save_appetite } from '../../../services';

// Score range is a 5×5 matrix → 1–25.
const scoreRange = (v) => {
    const n = Number(v);
    return (Number.isFinite(n) && n >= 1 && n <= 25) || 'Score must be between 1 and 25.';
};

// AppetiteForm — Add a tolerance threshold. CommonModal owns the form. Saves via
// risk_portal_save_appetite (upsert). Page owns the toast; this reports via
// callbacks. Payload: { category_id, appetite_statement, max_inherent_score,
// max_residual_score, max_aggregate_ale }.
//
// Props: show, onClose, onSuccess(msg), onError(msg), categories (for the picker).
export default function AppetiteForm({ show, onClose, onSuccess, onError, categories = [] }) {
    const [busy, setBusy] = useState(false);

    // Global (no category) + one option per category. Value is the category id
    // as a string; '' → Global.
    const categoryOptions = [
        { value: '', label: 'Global (all categories)' },
        ...categories.map((c) => ({ value: String(c.id), label: c.name })),
    ];

    const submit = async (data) => {
        setBusy(true);
        try {
            const payload = {
                category_id: data.category_id ? parseInt(data.category_id, 10) : null,
                appetite_statement: (data.appetite_statement || '').trim(),
                max_inherent_score: Number(data.max_inherent_score) || 15,
                max_residual_score: Number(data.max_residual_score) || 9,
                max_aggregate_ale: data.max_aggregate_ale ? Number(data.max_aggregate_ale) : null,
            };
            await risk_portal_save_appetite(payload);
            onSuccess?.('Appetite threshold saved');
            onClose?.();
        } catch (e) {
            onError?.(e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Failed to save threshold');
        } finally {
            setBusy(false);
        }
    };

    return (
        <CommonModal
            show={show}
            onHide={onClose}
            mid
            title="Add Appetite Threshold"
            subtitle="Set the maximum tolerated inherent/residual score (and optional ALE) for a category — or Global for the whole organisation."
            fields={[
                { name: 'category_id', label: 'Category', type: 'select', half: true, options: categoryOptions },
                { name: 'appetite_statement', label: 'Statement', type: 'text', half: true, placeholder: 'e.g. Low tolerance for financial risks' },
                { name: 'max_inherent_score', label: 'Max Inherent Score', type: 'number', half: true, required: 'Max inherent score is required.', validate: scoreRange },
                { name: 'max_residual_score', label: 'Max Residual Score', type: 'number', half: true, required: 'Max residual score is required.', validate: scoreRange },
                { name: 'max_aggregate_ale', label: 'Max ALE ($)', type: 'number', placeholder: 'optional — annualised loss expectancy cap' },
            ]}
            defaultValues={{ category_id: '', appetite_statement: '', max_inherent_score: 15, max_residual_score: 9, max_aggregate_ale: '' }}
            onSubmit={submit}
            buttons={[
                { text: 'Cancel', variant: 'secondary', onClick: onClose },
                { text: 'Save Threshold', variant: 'primary', type: 'submit' },
            ]}
        >
            {busy && <CommonLoader overlay label="Saving…" />}
        </CommonModal>
    );
}
